// TensorFlow Keras Layer Parameters
// Contains the official parameters for each layer type according to TensorFlow documentation

/**
 * Parameter definitions for TensorFlow Keras layers
 * Based on official TF documentation: https://www.tensorflow.org/api_docs/python/tf/keras/layers
 */

// Core Layers
export const DENSE_PARAMS = {
  units: {
    type: 'number',
    required: true,
    description: 'Positive integer, dimensionality of the output space.'
  },
  activation: {
    type: 'activation',
    description: 'Activation function to use.'
  },
  use_bias: {
    type: 'boolean',
    default: true,
    description: 'Boolean, whether the layer uses a bias vector.'
  },
  kernel_initializer: {
    type: 'initializer',
    default: 'glorot_uniform',
    description: 'Initializer for the kernel weights matrix.'
  },
  bias_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the bias vector.'
  },
  kernel_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the kernel weights matrix.'
  },
  bias_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the bias vector.'
  },
  activity_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the output of the layer.'
  },
  kernel_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the kernel weights matrix.'
  },
  bias_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the bias vector.'
  }
};

export const DROPOUT_PARAMS = {
  rate: {
    type: 'float',
    required: true,
    range: [0, 1],
    description: 'Float between 0 and 1. Fraction of the input units to drop.'
  },
  noise_shape: {
    type: 'shape',
    description: '1D integer tensor representing the shape of the binary dropout mask.'
  },
  seed: {
    type: 'integer',
    description: 'A Python integer to use as random seed.'
  }
};

export const ACTIVATION_PARAMS = {
  function: {
    type: 'activation',
    required: true,
    description: 'Name of activation function to use.'
  }
};

export const BATCH_NORMALIZATION_PARAMS = {
  axis: {
    type: 'integer',
    default: -1,
    description: 'The axis that should be normalized (typically the features axis).'
  },
  momentum: {
    type: 'float',
    default: 0.99,
    range: [0, 1],
    description: 'Momentum for the moving average.'
  },
  epsilon: {
    type: 'float',
    default: 0.001,
    description: 'Small float added to variance to avoid dividing by zero.'
  },
  center: {
    type: 'boolean',
    default: true,
    description: 'If True, add offset of beta to normalized tensor.'
  },
  scale: {
    type: 'boolean',
    default: true,
    description: 'If True, multiply by gamma. If False, gamma is not used.'
  },
  beta_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the beta weight.'
  },
  gamma_initializer: {
    type: 'initializer',
    default: 'ones',
    description: 'Initializer for the gamma weight.'
  },
  moving_mean_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the moving mean.'
  },
  moving_variance_initializer: {
    type: 'initializer',
    default: 'ones',
    description: 'Initializer for the moving variance.'
  }
};

export const LAYER_NORMALIZATION_PARAMS = {
  axis: {
    type: 'integer',
    default: -1,
    description: 'The axis or axes that should be normalized.'
  },
  epsilon: {
    type: 'float',
    default: 0.001,
    description: 'Small float added to variance to avoid dividing by zero.'
  },
  center: {
    type: 'boolean',
    default: true,
    description: 'If True, add offset of beta to normalized tensor.'
  },
  scale: {
    type: 'boolean',
    default: true,
    description: 'If True, multiply by gamma. If False, gamma is not used.'
  },
  beta_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the beta weight.'
  },
  gamma_initializer: {
    type: 'initializer',
    default: 'ones',
    description: 'Initializer for the gamma weight.'
  }
};

// Recurrent Layers
export const LSTM_PARAMS = {
  units: {
    type: 'number',
    required: true,
    description: 'Positive integer, dimensionality of the output space.'
  },
  activation: {
    type: 'activation',
    default: 'tanh',
    description: 'Activation function to use.'
  },
  recurrent_activation: {
    type: 'activation',
    default: 'sigmoid',
    description: 'Activation function to use for the recurrent step.'
  },
  use_bias: {
    type: 'boolean',
    default: true,
    description: 'Boolean, whether the layer uses a bias vector.'
  },
  kernel_initializer: {
    type: 'initializer',
    default: 'glorot_uniform',
    description: 'Initializer for the kernel weights matrix.'
  },
  recurrent_initializer: {
    type: 'initializer',
    default: 'orthogonal',
    description: 'Initializer for the recurrent kernel weights matrix.'
  },
  bias_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the bias vector.'
  },
  unit_forget_bias: {
    type: 'boolean',
    default: true,
    description: 'If True, add 1 to the bias of the forget gate at initialization.'
  },
  kernel_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the kernel weights matrix.'
  },
  recurrent_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the recurrent kernel weights matrix.'
  },
  bias_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the bias vector.'
  },
  activity_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the output of the layer.'
  },
  kernel_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the kernel weights matrix.'
  },
  recurrent_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the recurrent kernel weights matrix.'
  },
  bias_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the bias vector.'
  },
  dropout: {
    type: 'float',
    default: 0.0,
    range: [0, 1],
    description: 'Float between 0 and 1. Fraction of the units to drop for the linear transformation of the inputs.'
  },
  recurrent_dropout: {
    type: 'float',
    default: 0.0,
    range: [0, 1],
    description: 'Float between 0 and 1. Fraction of the units to drop for the linear transformation of the recurrent state.'
  },
  return_sequences: {
    type: 'boolean',
    default: false,
    description: 'Boolean. Whether to return the last output. in the output sequence, or the full sequence.'
  },
  return_state: {
    type: 'boolean',
    default: false,
    description: 'Boolean. Whether to return the last state in addition to the output.'
  },
  go_backwards: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, process the input sequence backwards and return the reversed sequence.'
  },
  stateful: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, the last state for each sample at index i in a batch will be used as initial state for the sample of index i in the following batch.'
  },
  time_major: {
    type: 'boolean',
    default: false,
    description: 'The shape format of the inputs and outputs tensors. If True, the inputs and outputs will be in shape (timesteps, batch, ...), whereas in the False case, it will be (batch, timesteps, ...).'
  },
  unroll: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, the network will be unrolled, else a symbolic loop will be used. Unrolling can speed-up a RNN, although it tends to be more memory-intensive.'
  }
};

export const GRU_PARAMS = {
  units: {
    type: 'number',
    required: true,
    description: 'Positive integer, dimensionality of the output space.'
  },
  activation: {
    type: 'activation',
    default: 'tanh',
    description: 'Activation function to use.'
  },
  recurrent_activation: {
    type: 'activation',
    default: 'sigmoid',
    description: 'Activation function to use for the recurrent step.'
  },
  use_bias: {
    type: 'boolean',
    default: true,
    description: 'Boolean, whether the layer uses a bias vector.'
  },
  kernel_initializer: {
    type: 'initializer',
    default: 'glorot_uniform',
    description: 'Initializer for the kernel weights matrix.'
  },
  recurrent_initializer: {
    type: 'initializer',
    default: 'orthogonal',
    description: 'Initializer for the recurrent kernel weights matrix.'
  },
  bias_initializer: {
    type: 'initializer',
    default: 'zeros',
    description: 'Initializer for the bias vector.'
  },
  kernel_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the kernel weights matrix.'
  },
  recurrent_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the recurrent kernel weights matrix.'
  },
  bias_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the bias vector.'
  },
  activity_regularizer: {
    type: 'regularizer',
    description: 'Regularizer function applied to the output of the layer.'
  },
  kernel_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the kernel weights matrix.'
  },
  recurrent_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the recurrent kernel weights matrix.'
  },
  bias_constraint: {
    type: 'constraint',
    description: 'Constraint function applied to the bias vector.'
  },
  dropout: {
    type: 'float',
    default: 0.0,
    range: [0, 1],
    description: 'Float between 0 and 1. Fraction of the units to drop for the linear transformation of the inputs.'
  },
  recurrent_dropout: {
    type: 'float',
    default: 0.0,
    range: [0, 1],
    description: 'Float between 0 and 1. Fraction of the units to drop for the linear transformation of the recurrent state.'
  },
  return_sequences: {
    type: 'boolean',
    default: false,
    description: 'Boolean. Whether to return the last output. in the output sequence, or the full sequence.'
  },
  return_state: {
    type: 'boolean',
    default: false,
    description: 'Boolean. Whether to return the last state in addition to the output.'
  },
  go_backwards: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, process the input sequence backwards and return the reversed sequence.'
  },
  stateful: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, the last state for each sample at index i in a batch will be used as initial state for the sample of index i in the following batch.'
  },
  unroll: {
    type: 'boolean',
    default: false,
    description: 'Boolean (default False). If True, the network will be unrolled, else a symbolic loop will be used. Unrolling can speed-up a RNN, although it tends to be more memory-intensive.'
  },
  reset_after: {
    type: 'boolean',
    default: true,
    description: 'GRU convention (whether to apply reset gate after or before matrix multiplication). False = "before", True = "after" (default).'
  }
};

// Convolutional Layers
export const CONV1D_PARAMS = {
  filters: {
    type: 'number',
    required: true,
    description: 'Integer, the dimensionality of the output space (i.e. the number of output filters in the convolution).'
  },
  kernel_size: {
    type: 'number',
    required: true,
    description: 'An integer or tuple/list of a single integer, specifying the length of the 1D convolution window.'
  },
  strides: {
    type: 'number',
    default: 1,
    description: 'An integer or tuple/list of a single integer, specifying the stride length of the convolution.'
  },
  padding: {
    type: 'select',
    default: 'valid',
    options: ['valid', 'same', 'causal'],
    description: 'One of "valid", "same" or "causal" (case-insensitive).'
  },
  dilation_rate: {
    type: 'number',
    default: 1,
    description: 'An integer or tuple/list of a single integer, specifying the dilation rate to use for dilated convolution.'
  },
  activation: {
    type: 'activation',
    description: 'Activation function to use.'
  },
  use_bias: {
    type: 'boolean',
    default: true,
    description: 'Boolean, whether the layer uses a bias vector.'
  }
};

// Pooling Layers
export const MAX_POOLING1D_PARAMS = {
  pool_size: {
    type: 'number',
    default: 2,
    description: 'Integer, size of the max pooling window.'
  },
  strides: {
    type: 'number',
    description: 'Integer, factor by which to downscale. If null, it will default to pool_size.'
  },
  padding: {
    type: 'select',
    default: 'valid',
    options: ['valid', 'same'],
    description: 'One of "valid" or "same" (case-insensitive).'
  }
};

export const AVERAGE_POOLING1D_PARAMS = {
  pool_size: {
    type: 'number',
    default: 2,
    description: 'Integer, size of the average pooling window.'
  },
  strides: {
    type: 'number',
    description: 'Integer, factor by which to downscale. If null, it will default to pool_size.'
  },
  padding: {
    type: 'select',
    default: 'valid',
    options: ['valid', 'same'],
    description: 'One of "valid" or "same" (case-insensitive).'
  }
};

export const GLOBAL_POOLING_PARAMS = {
  // Global pooling layers don't have specific parameters
};

// Utility Layers
export const FLATTEN_PARAMS = {
  data_format: {
    type: 'select',
    default: null,
    options: [null, 'channels_last', 'channels_first'],
    description: 'A string, one of channels_last (default) or channels_first.'
  }
};

export const RESHAPE_PARAMS = {
  target_shape: {
    type: 'shape',
    required: true,
    description: 'Target shape. Tuple of integers, does not include the batch axis.'
  }
};

// Export a mapping of node types to their parameter definitions
export const TENSORFLOW_PARAMETERS = {
  // Core Layers
  'Dense': DENSE_PARAMS,
  'Dropout': DROPOUT_PARAMS,
  'Activation': ACTIVATION_PARAMS,
  'BatchNormalization': BATCH_NORMALIZATION_PARAMS,
  'LayerNormalization': LAYER_NORMALIZATION_PARAMS,
  
  // Recurrent Layers
  'LSTM': LSTM_PARAMS,
  'GRU': GRU_PARAMS,
  
  // Convolutional Layers
  'Conv1D': CONV1D_PARAMS,
  
  // Pooling Layers
  'MaxPooling1D': MAX_POOLING1D_PARAMS,
  'AveragePooling1D': AVERAGE_POOLING1D_PARAMS,
  'GlobalMaxPooling1D': GLOBAL_POOLING_PARAMS,
  'GlobalAveragePooling1D': GLOBAL_POOLING_PARAMS,
  
  // Utility Layers
  'Flatten': FLATTEN_PARAMS,
  'Reshape': RESHAPE_PARAMS,
};

// Function to get parameters for a specific layer type
export const getLayerParameters = (layerType) => {
  return TENSORFLOW_PARAMETERS[layerType] || {};
};

// Function to get a subset of parameters based on complexity level
export const getBasicParameters = (layerType) => {
  const allParams = getLayerParameters(layerType);
  const basicParams = {};
  
  // Define which parameters should be considered "basic" for each layer type
  const basicParamKeys = {
    'Dense': ['units', 'activation', 'use_bias'],
    'Dropout': ['rate'],
    'LSTM': ['units', 'activation', 'recurrent_activation', 'return_sequences', 'return_state', 'dropout', 'recurrent_dropout'],
    'GRU': ['units', 'activation', 'recurrent_activation', 'return_sequences', 'return_state', 'dropout', 'recurrent_dropout'],
    'Conv1D': ['filters', 'kernel_size', 'strides', 'padding', 'activation'],
    'MaxPooling1D': ['pool_size', 'strides', 'padding'],
    'AveragePooling1D': ['pool_size', 'strides', 'padding'],
  };
  
  // Get the basic parameters for this layer type
  const keys = basicParamKeys[layerType] || Object.keys(allParams);
  
  // Filter the parameters to only include the basic ones
  keys.forEach(key => {
    if (allParams[key]) {
      basicParams[key] = allParams[key];
    }
  });
  
  return basicParams;
}; 